/**
 * Standalone consent route — `/me/automation-help` for a person who reached import/connect
 * without answering How should we help?
 */
import { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { automationApi, type AutomationCatalog } from '@/api/automation';
import { ErrorState } from '@/components/ErrorState';
import { useMe } from '@/session/meHooks';
import { PageHeader } from '@/shell/PageHeader';
import { StandaloneMain } from '@/shell/StandaloneMain';
import { usePageTitle } from '@/shell/usePageTitle';
import { ConsentStep } from './ConsentStep';

export function ConsentHelpPage() {
  const { t } = useTranslation();
  const me = useMe();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState<AutomationCatalog | null>(null);
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);

  const title = usePageTitle({ title: t('consent.title') });

  useEffect(() => {
    if (me.user.id === null) return;
    let cancelled = false;
    void automationApi
      .get()
      .then((next) => {
        if (cancelled) return;
        if (next.reviewedAt !== null) {
          void navigate('/me/accounts', { replace: true });
          return;
        }
        setCatalog(next);
        setLoading(false);
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setFailure({ error });
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [me.user.id, navigate, attempt]);

  // The page's `h1` is the title; the step renders without its own (`showTitle={false}`).
  let body;
  if (me.user.id === null) {
    body = (
      <>
        <Alert severity="info">{t('consent.noProfile')}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => void navigate('/me/accounts')}>
          {t('consent.continue')}
        </Button>
      </>
    );
  } else if (loading) {
    body = (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress aria-label={t('common.loading')} />
      </Box>
    );
  } else if (failure !== null || catalog === null) {
    body = (
      <ErrorState
        error={failure?.error}
        what={t('errors.what.automationChoices')}
        onRetry={() => {
          setFailure(null);
          setLoading(true);
          setAttempt((n) => n + 1);
        }}
      />
    );
  } else {
    body = (
      <ConsentStep
        variant="full"
        showTitle={false}
        catalog={catalog}
        onCatalogChange={setCatalog}
        onCompleted={() => void navigate(-1)}
      />
    );
  }

  return (
    // Outside the shell (it sits under the entry gate), so it brings its own `main` and skip link.
    <StandaloneMain maxWidth="md" sx={{ py: 3 }}>
      <PageHeader title={title} />
      {body}
    </StandaloneMain>
  );
}
